import { NgTemplateOutlet } from '@angular/common';
import {
    booleanAttribute,
    Component,
    computed,
    contentChildren,
    HostBinding,
    inject,
    input,
    numberAttribute,
} from '@angular/core';
import { ActionOptionDirective } from '@app/components/_ui/custom-card/action-option.directive';
import { TBorder, TColors } from '@app/interfaces/root-type.interface';
import { AppStyles } from '@app/services/app-styles.service';
import { translations } from '@app/utils/translations';
import { TranslateModule } from '@ngx-translate/core';
import { CardModule } from 'primeng/card';

@Component({
    selector: 'custom-card',
    imports: [TranslateModule, CardModule, NgTemplateOutlet],
    template: `
        <p-card class="w-full" [style]="ngStyle()">
            <ng-content></ng-content>

            @if (header(); as header) {
                <ng-template pTemplate="header">
                    <div
                        class="flex flex-row header"
                        [class.remove-padding-header]="removePaddingHeader()">
                        <div class="flex flex-col w-full">
                            <div class="title" [class.lg]="headerSize() === 'lg'">
                                {{ header | translate }}
                            </div>
                            @if (subHeader()) {
                                <div class="sub-title" [class.lg]="headerSize() === 'lg'">
                                    {{ subHeader()! | translate }}
                                </div>
                            }
                        </div>

                        <div class="flex-grow"></div>

                        <div class="flex items-center gap-item">
                            @for (header of actionOptions(); track header) {
                                <ng-container [ngTemplateOutlet]="header.template" />
                            }
                        </div>
                    </div>
                </ng-template>
            }
        </p-card>
    `,
    styles: `
        :host {
            .header {
                @apply p-md pb-0;

                &.remove-padding-header {
                    @apply p-0;
                }
            }

            ::ng-deep .p-card {
                @apply overflow-hidden border;
            }

            &.remove-padding {
                ::ng-deep .p-card-body {
                    @apply p-0 pt-md;
                }
            }
        }
    `,
})
export class CustomCardComponent {
    protected readonly translations = translations;

    private appStyles = inject(AppStyles);

    header = input<string>();
    subHeader = input<string>();
    headerSize = input<'lg'>();
    removePadding = input<boolean, unknown>(false, { transform: booleanAttribute });
    removePaddingHeader = input<boolean, unknown>(false, { transform: booleanAttribute });
    backgroundColor = input<TColors>();
    borderWidth = input<TBorder>();
    width = input<number, unknown>(0, { transform: numberAttribute });

    actionOptions = contentChildren(ActionOptionDirective);

    ngStyle = computed(() => {
        return {
            background: this.backgroundColor()
                ? this.appStyles.getRootVar(this.backgroundColor()!)
                : '',
            borderWidth: this.borderWidth()
                ? this.appStyles.getRootVar('border-' + this.borderWidth()!)
                : '',
            width: this.width() ? this.width() + 'px' : '',
        };
    });

    @HostBinding('class.remove-padding')
    get removePaddingClass() {
        return this.removePadding();
    }

    @HostBinding('class.remove-padding-header')
    get removePaddingHeaderClass() {
        return this.removePaddingHeader();
    }
}
